import {i18n} from '@online-offer/i18n';
import {
  Box,
  Heading,
  Hide,
  HStack,
  Image,
  Show,
  Space,
  Stack,
  Text,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';

import {Button} from '../../components/Button/Button';
import {Separator} from '../../components/Separator/Separator';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {canShowCebiaAutotracer} from './utils/canShowCebiaAutotracer';

const CEBIA_LOGO_SRC = '../../assets/images/cebia-logo.svg';
const CEBIA_LOGO_ASPECT_RATIO = 101 / 32;

export function CebiaAutotracer() {
  const {cebiaSmartCodeUrl} = useGetOnlineOfferData();

  // Only shown when the dealer published the Cebia Autotracer report for this vehicle;
  // bail on unsafe URLs (defense-in-depth, mirrors the CarPass banner guard).
  if (!canShowCebiaAutotracer(cebiaSmartCodeUrl)) {
    return null;
  }

  const handleView = () => {
    window.open(cebiaSmartCodeUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Box
        overflow="hidden"
        position="relative"
        borderRadius="medium"
        backgroundColor="palettes.blue.10.100"
        paddingVertical={[6, 8]}
        paddingHorizontal={[6, 8]}
        data-testid="cebiaAutotracer"
      >
        <Hide onMobile>
          <WorldMapBackground />
        </Hide>
        <Show onMobile>
          <WorldMapBackgroundMobile />
        </Show>
        <Box position="relative">
          {/* Mobile: stacked per Figma (heading + logo row, description, full-width button). */}
          <Show onMobile>
            <VStack spacing={6}>
              <VStack spacing={2}>
                <HStack spacing={6} align="center" justify="space-between">
                  <Heading size={4}>{i18n.t('cebiaAutotracer.title')}</Heading>
                  <Box flexShrink={0}>
                    <Image
                      isLazy
                      src={CEBIA_LOGO_SRC}
                      alt="Cebia"
                      ratio={CEBIA_LOGO_ASPECT_RATIO}
                      width="100%"
                      maxWidth="3.9375rem"
                      maxHeight={5}
                      fit="contain"
                    />
                  </Box>
                </HStack>
                <Text size="xSmall" color="secondary">
                  {i18n.t('cebiaAutotracer.description')}
                </Text>
              </VStack>
              {/* Row wrapper: isFullWidth sets flex:1, which would collapse the button's
                  height inside the column VStack (same reason CarPass wraps it too). */}
              <HStack>
                <Button
                  title={i18n.t('cebiaAutotracer.view')}
                  variant="inverted"
                  rightIcon="action/open_in_new"
                  isFullWidth
                  onClick={handleView}
                  data-testid="cebiaAutotracerView"
                />
              </HStack>
            </VStack>
          </Show>
          {/* Tablet+ desktop: single centered row (logo | heading+description | button). */}
          <Hide onMobile>
            <Stack direction="row" spacing={10} align="center">
              <Box flexShrink={0}>
                <Image
                  isLazy
                  src={CEBIA_LOGO_SRC}
                  alt="Cebia"
                  ratio={CEBIA_LOGO_ASPECT_RATIO}
                  width="100%"
                  maxWidth="5.5625rem"
                  maxHeight={7}
                  fit="contain"
                />
              </Box>
              <Box flexGrow={1}>
                <VStack spacing={2} align="flex-start">
                  <Heading size={3}>{i18n.t('cebiaAutotracer.title')}</Heading>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('cebiaAutotracer.description')}
                  </Text>
                </VStack>
              </Box>
              <Button
                title={i18n.t('cebiaAutotracer.view')}
                variant="inverted"
                rightIcon="action/open_in_new"
                onClick={handleView}
                data-testid="cebiaAutotracerView"
              />
            </Stack>
          </Hide>
        </Box>
      </Box>
      <Space vertical={8} />
      <Separator />
      <Space vertical={8} />
    </>
  );
}

// Decorative dotted world map (dots are palettes.blue.30.100 baked into the SVG) at its
// natural size, absolutely positioned at the fixed Figma offset. On narrow viewports the
// banner's overflow clipping cuts it at the right edge and the opaque button floats above
// it — it never slides left into the text.
// eslint-disable-next-line eag/no-css-property
const WorldMapBackground = styled.div`
  position: absolute;
  pointer-events: none;
  inset: 0 0 0 ${({theme}) => theme.getSize(124)};
  background: url('../../assets/images/cebia-world-map.svg') no-repeat left center;
`;

// Mobile per Figma: a dedicated zoomed map crop at its natural size, absolutely anchored
// to the banner's bottom-right corner (the crop already starts below the Cebia logo and
// reaches under the description text).
// eslint-disable-next-line eag/no-css-property
const WorldMapBackgroundMobile = styled.div`
  position: absolute;
  pointer-events: none;
  inset: 0;
  background: url('../../assets/images/cebia-world-map-mobile.svg') no-repeat right bottom;
`;
